import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TableRow, TableCell, Box, Typography, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DesktopActions from '../components/DesktopActions';
import { openOrderWindow } from '../../../store/actions/orders';
import { deleteMarketWatchInstrumentAction, setInitialTrackIndexAction } from '../../../store/actions/watchlist';
import { mapOrderWindowData, shouldEnableOptionChain } from '../../../services/utilities';
import octopusInstance from '../../../services/octopus/octopusInstance';
import { getExchangeCode, getExchangeName, exchangeSpecificStringifiedNumber } from '../../../services/exchanges';
import { formatPriceWithPrecision } from '../../../utils/utilfunction';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';

const MarketWatchExpandedRow = ({ instrument, columns, watchListIndex, watchlists }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [localInstrument, setLocalInstrument] = useState(instrument);
  const [isHovered, setIsHovered] = useState(false);

  const { exchangeName, isCDS, isFno } = useMemo(() => {
    const name = typeof localInstrument.exchange === 'number' 
      ? getExchangeName(localInstrument.exchange) 
      : (localInstrument.exchange || "");
    
    return {
      exchangeName: name,
      isCDS: name === 'CDS' || name === 'DCSX',
      isFno: name === 'NFO' || name === 'MCX'
    };
  }, [localInstrument.exchange]);

  const { userSettings } = useSelector((state) => state.profile);

  const handleBuy = useCallback((instrument, e) => {
    e?.stopPropagation();
    const orderData = mapOrderWindowData(instrument, "BUY");
    dispatch(openOrderWindow(orderData));
  }, [dispatch]);

  const handleSell = useCallback((instrument, e) => {
    e?.stopPropagation();
    const orderData = mapOrderWindowData(instrument, "SELL");
    dispatch(openOrderWindow(orderData));
  }, [dispatch]);

  const handleChart = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (localInstrument?.exchange && localInstrument?.token) {
      const precision = localInstrument?.price_precision || 0;
      navigate(`/overview/tdv/${localInstrument.exchange}/${localInstrument.token}/${precision}`, {
        state: { defaultTab: 'chart' }
      });
    }
  }, [localInstrument, navigate]);

  const handleDelete = useCallback(async (e) => {
    e?.stopPropagation();
    const mwId = watchlists[watchListIndex]?.id;
    
    if (mwId) {
      const payload = {
        marketWatchId: mwId,
        token: instrument.token,
        exchange: exchangeName,
        symbolName: instrument.trading_symbol,
        execution: instrument.execution === "SPREAD" ? "SPREAD" : "NORMAL",
      };
      
      const { status, message } = await dispatch(deleteMarketWatchInstrumentAction(
        payload, 
        Number(watchListIndex), 
        watchlists, 
        instrument
      ));
      
      if (status === "success") {
        enqueueSnackbar(message, { variant: "success", autoHideDuration: 1000 });
      } else {
        enqueueSnackbar(message || "Error deleting instrument", { variant: "error", autoHideDuration: 1000 });
      }
    }
  }, [dispatch, instrument, watchListIndex, watchlists, exchangeName]);


  useEffect(() => {
    // Subscribe to detailed market data for expanded view fields
    const handler = octopusInstance.wsHandler({
      messageType: "DetailMarketDataMessage",
      subscriptionLocation: `expanded-row-${instrument.token}`,
      payload: {
        exchangeCode: getExchangeCode(instrument.exchange),
        instrumentToken: instrument.token,
      },
    });

    handler.subscribe(({ msg }) => {
      // Map lastTradedPrice to ltp for consistency with the rest of the app
      const updates = { ...msg };
      if (msg.lastTradedPrice !== undefined) {
        updates.ltp = msg.lastTradedPrice;
      }
      
      setLocalInstrument((prev) => ({ ...prev, ...updates }));
    });

    return () => handler.unsubscribe();
  }, [instrument.token, instrument.exchange]);

  const formatValue = (value, field) => {
    if (value === undefined || value === null || value === 0 && field !== 'absoluteChange' && field !== 'percentChange') {
      if (field === 'lastTradedTime') return "-";
      return "0.00";
    }
    
    if (field === 'volume' || field === 'bestBidQuantity' || field === 'bestAskQuantity') {
      return value.toLocaleString();
    }

    if (field === 'lastTradedTime') {
      try {
        const timestamp = value.toString().length === 10 ? value * 1000 : value;
        return format(new Date(timestamp), 'HH:mm:ss');
      } catch (e) {
        return value;
      }
    }

    if (isCDS && localInstrument.price_precision !== undefined) {
      return formatPriceWithPrecision(value, localInstrument.price_precision, {
        showCurrency: false,
        showCommas: false,
        removeTrailingZeros: false
      });
    }

    return exchangeSpecificStringifiedNumber(exchangeName, value);
  };

  const getChangeColor = (value) => {
    if (value > 0) return "success.main";
    if (value < 0) return "error.main";
    return "text.secondary";
  };

  const renderSymbolCell = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {localInstrument.display_name || localInstrument.trading_symbol}
          </Typography>
          <Box
            sx={{
              fontSize: '10px',
              fontWeight: 'bold',
              px: 0.5,
              borderRadius: '4px',
              backgroundColor: 'action.hover',
              border: `1px solid ${theme.palette.divider}`,
              color: 'text.secondary',
              textTransform: 'uppercase'
            }}
          >
            {exchangeName}
          </Box>
        </Box>
        
        {/* Full Name / Description */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            fontSize: '0.75rem', 
            maxWidth: '200px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}
        >
          {isCDS ? localInstrument.company_name : (!isFno ? localInstrument.description : null)}
        </Typography>

        {/* Expiry for F&O */}
        {isFno && localInstrument.expiry && (
          <Typography variant="caption" sx={{ color: 'warning.main', fontSize: '10px', fontWeight: 600 }}>
            EXP: {format(new Date(localInstrument.expiry.toString().length === 10 ? localInstrument.expiry * 1000 : localInstrument.expiry), 'dd MMM yyyy')}
          </Typography>
        )}
      </Box>
    );
  };

  const enrichedInstrument = useMemo(() => ({
    ...localInstrument,
    exchange: exchangeName
  }), [localInstrument, exchangeName]);

  return (
    <TableRow 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ 
        position: 'relative',
        '&:hover': { backgroundColor: 'action.hover' } 
      }}
    >
      {columns.map((column, index) => (
        <TableCell key={column.field} align={column.align || 'left'} sx={{ whiteSpace: 'nowrap', py: 1.5 }}>
          {column.field === 'symbol' ? (
            renderSymbolCell()
          ) : column.field === 'ltp' ? (
            <Box sx={{ color: getChangeColor(localInstrument.absoluteChange), fontWeight: 600 }}>
              {formatValue(localInstrument.ltp, 'ltp')}
            </Box>
          ) : column.field === 'absoluteChange' ? (
            <Box sx={{ color: getChangeColor(localInstrument.absoluteChange) }}>
              {formatValue(localInstrument.absoluteChange, 'absoluteChange')}
            </Box>
          ) : column.field === 'percentChange' ? (
            <Box sx={{ color: getChangeColor(localInstrument.percentChange) }}>
              {formatValue(localInstrument.percentChange, 'percentChange')}%
            </Box>
          ) : (
            formatValue(localInstrument[column.field], column.field)
          )}

          {/* Render DesktopActions OVER the last cell area on hover */}
          {isHovered && index === columns.length - 1 && (
            <Box 
              sx={{ 
                position: 'absolute', 
                right: 16, 
                top: '50%', 
                transform: 'translateY(-50%)',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <DesktopActions
                instrument={enrichedInstrument}
                onBuy={handleBuy}
                onSell={handleSell}
                onMarketDepth={() => {}} 
                onDelete={handleDelete}
                onMore={() => {}} 
                onSaveToMarketwatch={() => {}}
                isInMarketwatch={true}
                userSettings={userSettings}
                showMarketDepth={false}
                showMore={false}
              />
            </Box>
          )}
        </TableCell>
      ))}
    </TableRow>
  );
};

export default MarketWatchExpandedRow;
